import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Menu,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useContext, useState, useEffect } from "react";
import InfinityCard from "./InfinityCard";
import CharacterCard from "./CharacterCard";
import CSVUploader from "./CSVUploader";
import EditCardModal from "./EditCardModal";
import { MainContext } from "../App";
import { db } from "../@shared/firebase/config";
import { addDoc, collection, getDocs, serverTimestamp } from "firebase/firestore";
import { getCurrentSeason } from "../@shared/utils/Seasons";
import { getCardImageStorageFolder } from "../@shared/utils/cardImageStoragePaths";
import CardImagePickerField from "./admin/CardImagePickerField";

const infinityAreas = [
  "Health",
  "Defense",
  "Attack",
  "Accuracy",
  "Turn Loss",
  "Player Hand",
  "Player Position",
  "All Stats",
];
const turnsActiveOptions = [
  "Instant",
  "1 Turn",
  "2 Turns",
  "3 Turns",
  "Instant + 1 Turn",
  "2 Turns / 1 Turn",
];
const rarityOptions = [
  { type: "Common", amt: 18 },
  { type: "Uncommon", amt: 19 },
  { type: "Rare", amt: 21 },
  { type: "Epic", amt: 23 },
  { type: "Legendary", amt: 25 },
];

const elementalOptions = [
  "fire",
  "water",
  "grass",
  "rock",
  "air",
  "electric",
  "void",
  "ice",
];

const trimOrNull = (value) => {
  const trimmed = String(value ?? "").trim();
  return trimmed.length ? trimmed : null;
};

const toBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }
  return !!value;
};

const parseCardNumber = (value) => {
  const parsed = Number.parseInt(String(value ?? "").trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const parseVersionCode = (value) => {
  const raw = String(value ?? "").trim();
  if (!/^\d+$/.test(raw)) return null;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const formatVersionCode = (value) => {
  const numeric = Number(value);
  const safe = Number.isFinite(numeric) && numeric > 0 ? numeric : 1;
  return String(Math.floor(safe)).padStart(3, "0");
};

const getCharacterVersionGroupValue = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const CardManager = ({
  mode = "default", // NEW: 'default' | 'character-version'
  seed = null, // NEW: { name, rarity, ipProviderStripeId, collectionStripeId, ... }
  onClose, // NEW
  onCreated, // NEW
}) => {
  const { setError, setSuccess, setMessage } = useContext(MainContext);
  const [cardType, setCardType] = useState("");

  // NEW: fields for version creation flow
  const [version3, setVersion3] = useState("");
  const [versionCardNumber, setVersionCardNumber] = useState("");
  const [versionMetaLoading, setVersionMetaLoading] = useState(false);
  const [artistName, setArtistName] = useState("");
  const [artistStripeId, setArtistStripeId] = useState("");
  const [artistBadge, setArtistBadge] = useState("");
  const activeSeason = getCurrentSeason();

  // Infinity (CSV/manual) card fields state
  const [infinityName, setInfinityName] = useState("");
  const [infinityDescription, setInfinityDescription] = useState("");
  const [infinityEffect, setInfinityEffect] = useState("");
  const [infinityAreaAffected, setInfinityAreaAffected] = useState([]);
  const [infinityTurnsActive, setInfinityTurnsActive] = useState("");
  const [infinityMax, setInfinityMax] = useState("");
  const [infinityFinite, setInfinityFinite] = useState(false);
  const [infinityCraftNeeded, setInfinityCraftNeeded] = useState("");
  const [infinityCraftAmount, setInfinityCraftAmount] = useState("");
  const [infinityAvailableForPurchase, setInfinityAvailableForPurchase] =
    useState(false);
  const [infinityPrice, setInfinityPrice] = useState("");
  const [infinitySeason, setInfinitySeason] = useState("");
  const [infinityImageUrl, setInfinityImageUrl] = useState("");
  const [infinityBgColor1, setInfinityBgColor1] = useState("");
  const [infinityBgColor2, setInfinityBgColor2] = useState("");

  // Character card fields state
  const [characterName, setCharacterName] = useState("");
  const [attackName, setAttackName] = useState("");
  const [characterRarity, setCharacterRarity] = useState("");
  const [characterDescription, setCharacterDescription] = useState("");
  const [characterCollectionName, setCharacterCollectionName] = useState("");
  const [characterTokenId, setCharacterTokenId] = useState("");
  const [characterAttack, setCharacterAttack] = useState("");
  const [characterAccuracy, setCharacterAccuracy] = useState("");
  const [characterDefense, setCharacterDefense] = useState("");
  const [characterHealth, setCharacterHealth] = useState("");
  const [characterElement, setCharacterElement] = useState("");
  const [characterFinite, setCharacterFinite] = useState(false);
  const [characterAvailableForPurchase, setCharacterAvailableForPurchase] =
    useState(false);
  const [characterPrice, setCharacterPrice] = useState("");
  const [characterSeason, setCharacterSeason] = useState("");
  const [characterImageUrl, setCharacterImageUrl] = useState("");
  const [imageBG, setImageBG] = useState("");
  const [imageStart, setImageStart] = useState("");
  const [imageFin, setImageFin] = useState("");
  const [imageHit, setImageHit] = useState("");
  const [characterBgColor1, setCharacterBgColor1] = useState("");
  const [characterBgColor2, setCharacterBgColor2] = useState("");
  const [collectionStripeId, setCollectionStripeId] = useState("");
  const [ipProviderStripeId, setIpProviderStripeId] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const [charMenuOpen, setCharMenuOpen] = useState(false);
  const [infinityMenuOpen, setInfinityMenuOpen] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [uploadedCards, setUploadedCards] = useState([]);

  // State for editing CSV-uploaded cards
  const [editingCard, setEditingCard] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Prefill when opening in character-version mode
  useEffect(() => {
    if (mode === "character-version" && seed) {
      // prefill character form using seed
      setCardType("character"); // force character view on the right preview
      setCharacterName(seed.name || "");
      setCharacterRarity(seed.rarity || "Common");
      setCharacterImageUrl(seed.imageUrl || "");
      setCharacterAttack(seed.attack || "");
      setCharacterAccuracy(seed.accuracy || "");
      setCharacterDefense(seed.defense || "");
      setCharacterHealth(seed.health || "");

      setCharacterBgColor1(seed.bgColor1 || "");
      setCharacterBgColor2(seed.bgColor2 || "");
      setAttackName(seed.attackName || "");
      setCharacterDescription(seed.description || "");
      setCharacterElement(seed.element || "");
      setCharacterAvailableForPurchase(
        toBoolean(seed.availForPurchase ?? seed.availableForPurchase),
      );
      setCharacterCollectionName(seed.collectionName || "");
      setCharacterTokenId(seed.tokenId || "");
      setCharacterFinite(toBoolean(seed.finite));
      setCharacterPrice(seed.price || "");
      setCollectionStripeId(
        seed.collectionCreatorStripeId || seed.collectionStripeId || "",
      );
      setIpProviderStripeId(seed.ipProviderStripeId || "");
      setImageBG(seed.imageBG || "");
      setImageStart(seed.imageStart || "");
      setImageFin(seed.imageFin || "");
      setImageHit(seed.imageHit || "");
      setVersionCardNumber("");
      setCharacterSeason(
        String(Number(activeSeason?.season ?? seed.season) || ""),
      );
    }
  }, [mode, seed, activeSeason?.season]);

  useEffect(() => {
    let cancelled = false;

    const preloadVersionMetadata = async () => {
      if (mode !== "character-version" || !seed) return;

      try {
        setVersionMetaLoading(true);
        const snapshot = await getDocs(collection(db, "Character cards"));
        if (cancelled) return;

        const cards = snapshot.docs.map((docSnap) => docSnap.data() || {});
        let maxCardNumber = 0;

        cards.forEach((card) => {
          const cardNumber = parseCardNumber(card.number ?? card.cardNumber);
          if (cardNumber && cardNumber > maxCardNumber) maxCardNumber = cardNumber;
        });

        setVersionCardNumber(String(maxCardNumber + 1));

        const targetName = getCharacterVersionGroupValue(seed.name);
        const targetCollection = getCharacterVersionGroupValue(
          seed.collectionName,
        );

        const versionCandidates = cards.filter((card) => {
          const cardName = getCharacterVersionGroupValue(card.name);
          if (!targetName || cardName !== targetName) return false;
          if (!targetCollection) return true;

          return getCharacterVersionGroupValue(card.collectionName) === targetCollection;
        });

        let maxVersion = parseVersionCode(seed.version);
        if (!maxVersion) maxVersion = 1;

        versionCandidates.forEach((card) => {
          const parsedVersion = parseVersionCode(card.version);
          if (parsedVersion) {
            maxVersion = Math.max(maxVersion, parsedVersion);
          } else {
            maxVersion = Math.max(maxVersion, 1);
          }
        });

        setVersion3(formatVersionCode(maxVersion + 1));
        setCharacterSeason(
          String(Number(activeSeason?.season ?? seed.season) || ""),
        );
      } catch (error) {
        console.error("Failed loading version metadata:", error);
        setVersion3((prev) =>
          prev || formatVersionCode((parseVersionCode(seed?.version) || 1) + 1),
        );
      } finally {
        if (!cancelled) setVersionMetaLoading(false);
      }
    };

    preloadVersionMetadata();

    return () => {
      cancelled = true;
    };
  }, [mode, seed, activeSeason?.season]);

  // --- CSV Cancel/Submit Handlers ---
  const handleCancelCSV = () => {
    setUploadedCards([]);
    setCardType("");
    setMessage("CSV upload cancelled.");
  };

  const handleUploadCard = async (type) => {
    let cardData =
      type === "character"
        ? {
            name: characterName,
            attackName: attackName,
            rarity: characterRarity,
            description: characterDescription,
            collectionName: characterCollectionName,
            tokenId: characterTokenId,
            attack: characterAttack,
            accuracy: characterAccuracy,
            defense: characterDefense,
            health: characterHealth,
            finite: characterFinite,
            availableForPurchase: characterAvailableForPurchase,
            price: characterPrice,
            season: characterSeason,
            imageUrl: characterImageUrl,
            imageBG: imageBG,
            imageStart: imageStart,
            imageFin: imageFin,
            imageHit: imageHit,
            bgColor1: characterBgColor1,
            bgColor2: characterBgColor2,
            element: characterElement,
          }
        : {
            name: infinityName,
            description: infinityDescription,
            effect: infinityEffect,
            areaAffected: infinityAreaAffected,
            turnsActive: infinityTurnsActive,
            max: infinityMax,
            finite: infinityFinite,
            craftNeeded: infinityCraftNeeded,
            craftAmount: infinityCraftAmount,
            availForPurchase: infinityAvailableForPurchase,
            price: infinityPrice,
            season: infinitySeason,
            imageUrl: infinityImageUrl,
            bgColor1: infinityBgColor1,
            bgColor2: infinityBgColor2,
          };

    try {
      // Determine collection based on type.
      const collectionName =
        type === "character" ? "Character cards" : "Infinity cards";
      await db.collection(collectionName).add(cardData);
      setSuccess(true);
      setMessage("Card uploaded successfully.");
      // Optionally clear any manual entry state here.
    } catch (error) {
      console.error("Firebase Upload Error:", error);
      setError(true);
      setMessage("Error uploading card to Firebase.");
    }
  };

  // Updated batch upload function that writes to the proper collection
  const handleUploadCards = async (cardsArray, type) => {
    try {
      const batch = db.batch();
      const collectionName =
        type === "character-csv" ? "Character cards" : "Infinity cards";
      const collectionRef = db.collection(collectionName);
      cardsArray.forEach((card) => {
        const docRef = collectionRef.doc();
        batch.set(docRef, card);
      });
      await batch.commit();
      setSuccess(true);
      setMessage("Cards uploaded successfully.");
      setUploadedCards([]);
      setCardType("");
    } catch (error) {
      console.error("Firebase Upload Error:", error);
      setError(true);
      setMessage("Error uploading cards to Firebase.");
    }
  };

  // --- Menu Handlers ---
  const handleClick = (event, type) => {
    setAnchorEl(event.currentTarget);
    if (type === "character") {
      setCharMenuOpen(true);
    } else if (type === "infinity") {
      setInfinityMenuOpen(true);
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
    setCharMenuOpen(false);
    setInfinityMenuOpen(false);
  };

  // --- CSV Upload Handler ---
  // Updated to accept a second parameter 'type'
  const handleUploadCSV = async (newCards, type) => {
    try {
      setUploading(true);
      newCards = newCards
        .map((event) => {
          let cleanedCards = {};
          Object.keys(event).forEach((key) => {
            cleanedCards[key.trim()] = event[key];
          });
          return cleanedCards;
        })
        .map((event) => {
          return transformCardData(event);
        });
      // If a card has an "areaAffected" field, split it into an array.
      newCards = newCards.map((card) => {
        return {
          ...card,
          areaAffected: card.areaAffected ? card.areaAffected.split(",") : [],
        };
      });
      console.log("NEW CARDS => ", newCards);
      setUploadedCards(newCards);
      // Set cardType based on type from CSVUploader
      setCardType(type);
    } catch (error) {
      console.error("Upload Error:", error);
      setError(true);
      setMessage("Error processing events.");
    }
    setUploading(false);
  };

  const toCamelCase = (str) => {
    const formattedKey = str
      .replace(/[^a-zA-Z0-9 ]/g, "")
      .replace(/\s(.)/g, (match) => match.toUpperCase())
      .replace(/\s/g, "")
      .replace(/^(.)/, (match) => match.toLowerCase());
    return formattedKey;
  };

  const transformCardData = (event) => {
    const transformedCard = {};
    for (const key in event) {
      const newKey = toCamelCase(key);
      transformedCard[newKey] = event[key];
    }
    return transformedCard;
  };

  useEffect(() => {}, [uploadedCards]);

  // NEW: submit handler for “character-version” flow
  const handleCreateCharacterVersion = async () => {
    try {
      // Enforce 3 digits (e.g., "001", "042", "123")
      const v = String(version3 || "").trim();
      if (!/^\d{3}$/.test(v)) {
        setError(true);
        setMessage("Version must be a 3-digit number (e.g., 001).");
        console.log("Version must be a 3-digit number (e.g., 001).");
        return;
      }
      const parsedNumber = parseCardNumber(versionCardNumber);
      if (!parsedNumber) {
        setError(true);
        setMessage("Card number could not be auto-assigned. Please retry.");
        return;
      }
      // if (!seed?.ipProviderStripeId || !seed?.collectionStripeId) {
      //   setError(true);
      //   setMessage("Missing Stripe IDs on the seed data.");
      //   return;
      // }

      const parsedSeason = Number(characterSeason);
      const seasonValue = Number.isFinite(parsedSeason) ? parsedSeason : null;
      const resolvedCollectionStripeId =
        seed.collectionCreatorStripeId ||
        seed.collectionStripeId ||
        collectionStripeId;

      // Everything goes into SAME collection "Character cards"
      const payload = {
        // carry-overs (seed)
        name: trimOrNull(seed.name || characterName) || "Unnamed",
        number: parsedNumber,
        cardNumber: String(parsedNumber),
        rarity: seed.rarity || characterRarity || "Common",
        ipProviderStripeId: trimOrNull(seed.ipProviderStripeId || ipProviderStripeId),
        collectionStripeId: trimOrNull(resolvedCollectionStripeId),
        collectionCreatorStripeId: trimOrNull(resolvedCollectionStripeId),
        collectionName: trimOrNull(seed.collectionName || characterCollectionName),
        tokenId: trimOrNull(characterTokenId),
        element: trimOrNull(seed.element || characterElement),

        // base stats (you can let them be adjusted if you want)
        attack: Number(characterAttack) || 0,
        accuracy: Number(characterAccuracy) || 0,
        defense: Number(characterDefense) || 0,
        health: Number(characterHealth) || 0,

        // typical card fields you already use
        attackName: attackName || null,
        description: characterDescription || "",
        imageUrl: characterImageUrl || "",
        imageBG: imageBG || "",
        imageStart: imageStart || seed.imageStart || "",
        imageFin: imageFin || seed.imageFin || "",
        imageHit: imageHit || seed.imageHit || "",
        bgColor1: characterBgColor1 || "",
        bgColor2: characterBgColor2 || "",
        season: seasonValue,
        finite: !!characterFinite,
        availableForPurchase: !!characterAvailableForPurchase,
        price: characterPrice !== "" ? Number(characterPrice) : null,

        // NEW versioning fields
        version: formatVersionCode(v),
        artistName: (artistName || "").trim() || null,
        artistStripeId: (artistStripeId || "").trim() || null,
        artistBadge: (artistBadge || "").trim() || null,

        // (optional) flags you may want
        isVersion: true, // helps query/filter if needed
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(db, "Character cards"), payload);
      setSuccess(true);
      setMessage("Version created.");
      console.log("success createing");
      onCreated?.();
    } catch (e) {
      console.error("error creating", e);
      setError(true);
      setMessage("Error creating version.");
    }
  };

  // ---------------- RENDER ----------------
  if (mode === "character-version") {
    const seasonSummary = activeSeason
      ? `${activeSeason.season} - ${activeSeason.name}`
      : characterSeason || "N/A";

    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(460px, 1fr) minmax(320px, 0.9fr)",
          },
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            New Character Version
          </Typography>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>Core Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="Character Name"
                    value={seed?.name || ""}
                    fullWidth
                    size="small"
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: 1,
                      bgcolor: "rgba(255,255,255,0.04)",
                    }}
                  >
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Number (auto-filled)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      #{versionCardNumber || "N/A"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Rarity"
                    value={seed?.rarity || "Common"}
                    fullWidth
                    size="small"
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Version (3 digits)"
                    value={version3}
                    onChange={(e) => setVersion3(e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ inputMode: "numeric", pattern: "\\d{3}" }}
                    helperText={
                      versionMetaLoading
                        ? "Calculating next version..."
                        : "Auto-filled from existing versions."
                    }
                    InputProps={{
                      endAdornment: versionMetaLoading ? (
                        <CircularProgress color="inherit" size={16} />
                      ) : null,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 1.25,
                      borderRadius: 1,
                      bgcolor: "rgba(255,255,255,0.04)",
                    }}
                  >
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Season (auto-filled)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {seasonSummary}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>Collection And Artist</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField
                    label="Collection Name"
                    value={characterCollectionName || ""}
                    fullWidth
                    size="small"
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="IP Provider Stripe ID"
                    value={seed?.ipProviderStripeId || ""}
                    fullWidth
                    size="small"
                    disabled
                    helperText="Locked to parent IP"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Collection Creator Stripe ID"
                    value={seed?.collectionCreatorStripeId || seed?.collectionStripeId || ""}
                    fullWidth
                    size="small"
                    disabled
                    helperText="Locked to collection"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Artist Name"
                    value={artistName}
                    onChange={(e) => setArtistName(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Artist Stripe ID"
                    value={artistStripeId}
                    onChange={(e) => setArtistStripeId(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Artist Badge"
                    value={artistBadge}
                    onChange={(e) => setArtistBadge(e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="e.g., LAS v1, Guest Artist"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>Stats And Economy</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField
                    label="Attack Name"
                    value={attackName}
                    onChange={(e) => setAttackName(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Attack"
                    type="number"
                    value={characterAttack}
                    onChange={(e) => setCharacterAttack(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Defense"
                    type="number"
                    value={characterDefense}
                    onChange={(e) => setCharacterDefense(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Health"
                    type="number"
                    value={characterHealth}
                    onChange={(e) => setCharacterHealth(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Accuracy"
                    type="number"
                    value={characterAccuracy}
                    onChange={(e) => setCharacterAccuracy(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    value={characterDescription}
                    onChange={(e) => setCharacterDescription(e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Price (ETH)"
                    type="number"
                    value={characterPrice}
                    onChange={(e) => setCharacterPrice(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={characterFinite}
                          onChange={(e) => setCharacterFinite(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Finite"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={characterAvailableForPurchase}
                          onChange={(e) =>
                            setCharacterAvailableForPurchase(e.target.checked)
                          }
                          size="small"
                        />
                      }
                      label="Available For Purchase"
                    />
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>Visual Assets</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <CardImagePickerField
                    label="Image"
                    value={characterImageUrl}
                    onChange={(url) => setCharacterImageUrl(url || "")}
                    storageFolder={getCardImageStorageFolder({
                      cardType: "character",
                      version: version3,
                      field: "imageUrl",
                    })}
                    disabled={versionMetaLoading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CardImagePickerField
                    label="Background Image"
                    value={imageBG}
                    onChange={(url) => setImageBG(url || "")}
                    storageFolder={getCardImageStorageFolder({
                      cardType: "character",
                      version: version3,
                      field: "imageBG",
                    })}
                    disabled={versionMetaLoading}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CardImagePickerField
                    label="Image Start"
                    value={imageStart}
                    onChange={(url) => setImageStart(url || "")}
                    storageFolder={getCardImageStorageFolder({
                      cardType: "character",
                      version: version3,
                      field: "imageStart",
                    })}
                    disabled={versionMetaLoading}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CardImagePickerField
                    label="Image Fin"
                    value={imageFin}
                    onChange={(url) => setImageFin(url || "")}
                    storageFolder={getCardImageStorageFolder({
                      cardType: "character",
                      version: version3,
                      field: "imageFin",
                    })}
                    disabled={versionMetaLoading}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CardImagePickerField
                    label="Image Hit"
                    value={imageHit}
                    onChange={(url) => setImageHit(url || "")}
                    storageFolder={getCardImageStorageFolder({
                      cardType: "character",
                      version: version3,
                      field: "imageHit",
                    })}
                    disabled={versionMetaLoading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Typography variant="body2" sx={{ minWidth: 70 }}>
                      Colors
                    </Typography>
                    <input
                      type="color"
                      value={characterBgColor1 || "#ffffff"}
                      onChange={(e) => setCharacterBgColor1(e.target.value)}
                      style={{
                        width: 56,
                        height: 36,
                        border: "none",
                        background: "none",
                      }}
                    />
                    <input
                      type="color"
                      value={characterBgColor2 || "#ffffff"}
                      onChange={(e) => setCharacterBgColor2(e.target.value)}
                      style={{
                        width: 56,
                        height: 36,
                        border: "none",
                        background: "none",
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Box sx={{ display: "flex", gap: 1, mt: 0.75 }}>
            <Button onClick={onClose} variant="outlined" fullWidth>
              Cancel
            </Button>
            <Button
              onClick={handleCreateCharacterVersion}
              variant="contained"
              fullWidth
              disabled={versionMetaLoading}
            >
              Create Version
            </Button>
          </Box>
        </Box>

        <Box>
          <Typography sx={{ mb: 1, fontWeight: 700 }}>Live Preview</Typography>
          <Box
            sx={{
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 2,
              p: 1,
              height: { xs: 520, md: 560 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.2)",
              overflow: "hidden",
            }}
          >
            <CharacterCard
              name={seed?.name || characterName}
              attackName={attackName}
              rarity={seed?.rarity || characterRarity}
              description={characterDescription}
              collectionName={characterCollectionName}
              tokenId={characterTokenId}
              attack={characterAttack}
              accuracy={characterAccuracy}
              defense={characterDefense}
              health={characterHealth}
              element={characterElement}
              finite={characterFinite}
              availableForPurchase={characterAvailableForPurchase}
              price={characterPrice}
              season={characterSeason}
              number={versionCardNumber}
              imageUrl={characterImageUrl}
              imageBG={imageBG || seed?.imageBG || ""}
              imageStart={imageStart || seed?.imageStart || ""}
              imageFin={imageFin || seed?.imageFin || ""}
              imageHit={imageHit || seed?.imageHit || ""}
              bgColor1={characterBgColor1 || "#fff"}
              bgColor2={characterBgColor2 || "#fff"}
              containerHeight={520}
              heightPer={100}
              version={version3}
              artistName={artistName}
              artistStripeId={artistStripeId}
              artistBadge={artistBadge}
              collectionStripeId={collectionStripeId}
            />
          </Box>

          <Box sx={{ mt: 1.25 }}>
            <Typography sx={{ mb: 0.75, fontSize: 12, opacity: 0.8 }}>
              Character Images
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1 }}>
              {[
                { key: "imageStart", label: "Start", src: imageStart || seed?.imageStart },
                { key: "imageFin", label: "Fin", src: imageFin || seed?.imageFin },
                { key: "imageHit", label: "Hit", src: imageHit || seed?.imageHit },
              ].map((asset) => (
                <Box
                  key={asset.key}
                  sx={{
                    borderRadius: 1,
                    border: "1px solid rgba(255,255,255,0.16)",
                    background: "rgba(0,0,0,0.25)",
                    aspectRatio: "1 / 1",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {asset.src ? (
                    <img
                      src={asset.src}
                      alt={asset.label}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {asset.label}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
  return (
    <Box display="flex" height="100%">
      {/* Left Section */}
      <Box
        width="40vw"
        bgcolor="white"
        p={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        overflow="auto"
      >
        {cardType === "" ? (
          <>
            <Button
              variant="outlined"
              id="character-btn"
              aria-controls={charMenuOpen ? "character-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={charMenuOpen ? "true" : undefined}
              onClick={(e) => handleClick(e, "character")}
              style={{ marginBottom: "8px", width: "80%" }}
              size="small"
            >
              Character Card
            </Button>
            <Menu
              id="character-menu"
              aria-labelledby="character-btn"
              anchorEl={anchorEl}
              open={charMenuOpen}
              onClose={handleClose}
            >
              <MenuItem
                onClick={() => {
                  setCardType("character");
                  handleClose();
                }}
              >
                Create Character Card
              </MenuItem>
              <MenuItem>
                <CSVUploader
                  onUpload={handleUploadCSV}
                  setCardType={setCardType}
                  type={"character-csv"}
                  close={handleClose}
                />
              </MenuItem>
            </Menu>

            <Button
              variant="outlined"
              id="infinity-btn"
              aria-controls={infinityMenuOpen ? "infinity-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={infinityMenuOpen ? "true" : undefined}
              onClick={(e) => handleClick(e, "infinity")}
              style={{ marginBottom: "8px", width: "80%" }}
              size="small"
            >
              Infinity Card
            </Button>
            <Menu
              id="infinity-menu"
              aria-labelledby="infinity-btn"
              anchorEl={anchorEl}
              open={infinityMenuOpen}
              onClose={handleClose}
            >
              <MenuItem
                onClick={() => {
                  setCardType("infinity");
                  handleClose();
                }}
              >
                Create Infinity Card
              </MenuItem>
              <MenuItem>
                <CSVUploader
                  onUpload={handleUploadCSV}
                  setCardType={setCardType}
                  type={"infinity-csv"}
                  close={handleClose}
                />
              </MenuItem>
            </Menu>
          </>
        ) : cardType === "infinity" ? (
          <>
            {/* Infinity Card Manual Entry Fields */}
            <TextField
              label="Name"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={infinityName}
              onChange={(e) => setInfinityName(e.target.value)}
            />
            <TextField
              label="Description"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={infinityDescription}
              onChange={(e) => setInfinityDescription(e.target.value)}
            />
            <TextField
              label="Effect"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={infinityEffect}
              onChange={(e) => setInfinityEffect(e.target.value)}
            />
            <Box display="flex" width="100%" gap="8px">
              <FormControl
                fullWidth
                margin="dense"
                size="small"
                variant="outlined"
              >
                <InputLabel id="area-affected-label">Area Affected</InputLabel>
                <Select
                  labelId="area-affected-label"
                  multiple
                  value={infinityAreaAffected}
                  onChange={(e) => {
                    const {
                      target: { value },
                    } = e;
                    setInfinityAreaAffected(
                      typeof value === "string" ? value.split(",") : value
                    );
                  }}
                  input={<OutlinedInput label="Area Affected" />}
                  renderValue={(selected) => selected.join(", ")}
                >
                  {infinityAreas.map((area) => (
                    <MenuItem key={area} value={area}>
                      <Checkbox
                        checked={infinityAreaAffected.indexOf(area) > -1}
                      />
                      <ListItemText primary={area} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                fullWidth
                margin="dense"
                size="small"
                variant="outlined"
              >
                <InputLabel id="turns-active-label">Turns Active</InputLabel>
                <Select
                  labelId="turns-active-label"
                  value={infinityTurnsActive}
                  onChange={(e) => setInfinityTurnsActive(e.target.value)}
                  label="Turns Active"
                >
                  {turnsActiveOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box display="flex" width="100%" alignItems="center" gap="8px">
              <TextField
                label="Max"
                variant="outlined"
                fullWidth
                margin="dense"
                size="small"
                type="number"
                value={infinityMax}
                onChange={(e) => setInfinityMax(e.target.value)}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={infinityFinite}
                    onChange={(e) => setInfinityFinite(e.target.checked)}
                    size="small"
                  />
                }
                label="Finite"
              />
            </Box>
            <Box display="flex" width="100%" gap="8px">
              <TextField
                label="Craft Needed"
                variant="outlined"
                fullWidth
                margin="dense"
                size="small"
                value={infinityCraftNeeded}
                onChange={(e) => setInfinityCraftNeeded(e.target.value)}
              />
              <TextField
                label="Craft Amount"
                variant="outlined"
                fullWidth
                margin="dense"
                size="small"
                type="number"
                value={infinityCraftAmount}
                onChange={(e) => setInfinityCraftAmount(e.target.value)}
              />
            </Box>
            <Box display="flex" width="100%" alignItems="center" gap="8px">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={infinityAvailableForPurchase}
                    onChange={(e) =>
                      setInfinityAvailableForPurchase(e.target.checked)
                    }
                    size="small"
                  />
                }
                label="Available for Purchase"
              />
              <TextField
                label="Price"
                variant="outlined"
                fullWidth
                margin="dense"
                size="small"
                type="number"
                value={infinityPrice}
                onChange={(e) => setInfinityPrice(e.target.value)}
                style={{ flex: 1 }}
              />
            </Box>
            <Box display="flex" width="100%" gap="8px">
              <TextField
                label="Season"
                variant="outlined"
                margin="dense"
                size="small"
                type="number"
                value={infinitySeason}
                onChange={(e) => setInfinitySeason(e.target.value)}
                style={{ width: "30%" }}
              />
              <TextField
                label="Image URL"
                variant="outlined"
                margin="dense"
                size="small"
                value={infinityImageUrl}
                onChange={(e) => setInfinityImageUrl(e.target.value)}
                style={{ width: "70%" }}
              />
            </Box>
            <Box display="flex" width="100%" gap="8px" mt={1}>
              <input
                type="color"
                value={infinityBgColor1}
                onChange={(e) => setInfinityBgColor1(e.target.value)}
                style={{
                  width: "30%",
                  height: "38px",
                  borderRadius: "5px",
                  border: "none",
                }}
              />
              <input
                type="color"
                value={infinityBgColor2}
                onChange={(e) => setInfinityBgColor2(e.target.value)}
                style={{
                  width: "30%",
                  height: "38px",
                  borderRadius: "5px",
                  border: "none",
                }}
              />
            </Box>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setCardType("")}
              style={{ marginTop: "16px", width: "80%" }}
              size="small"
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleUploadCard(cardType)}
              style={{ marginTop: "16px", width: "80%" }}
              size="small"
            >
              Submit
            </Button>
          </>
        ) : cardType === "character" ? (
          <>
            {/* Character Card Manual Entry Fields */}
            <Box display="flex" width="100%" gap="8px">
              <TextField
                label="Name"
                variant="outlined"
                margin="dense"
                size="small"
                value={characterName}
                onChange={(e) => setCharacterName(e.target.value)}
                style={{ flex: 2 }}
              />
              <FormControl
                margin="dense"
                variant="outlined"
                size="small"
                style={{ flex: 1 }}
              >
                <InputLabel id="rarity-label">Rarity</InputLabel>
                <Select
                  labelId="rarity-label"
                  value={characterRarity}
                  onChange={(e) => setCharacterRarity(e.target.value)}
                  label="Rarity"
                >
                  {rarityOptions.map((option) => (
                    <MenuItem key={option.type} value={option.type}>
                      {option.type} - {option.amt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Description"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              multiline
              rows={3}
              value={characterDescription}
              onChange={(e) => setCharacterDescription(e.target.value)}
            />
            <TextField
              label="attack name"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              multiline
              rows={3}
              value={attackName}
              onChange={(e) => setAttackName(e.target.value)}
            />
            <FormControl
              fullWidth
              margin="dense"
              size="small"
              variant="outlined"
            >
              <InputLabel id="element-label">Element</InputLabel>
              <Select
                labelId="element-label"
                value={characterElement}
                onChange={(e) => setCharacterElement(e.target.value)}
                label="Element"
              >
                {elementalOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Collection Name"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={characterCollectionName}
              onChange={(e) => setCharacterCollectionName(e.target.value)}
            />
            <Box display="flex" width="100%" gap="8px">
              <TextField
                label="Token Id"
                variant="outlined"
                margin="dense"
                size="small"
                type="number"
                value={characterTokenId}
                onChange={(e) => setCharacterTokenId(e.target.value)}
                style={{ flex: 1 }}
              />
              <TextField
                label="Attack"
                variant="outlined"
                margin="dense"
                size="small"
                type="number"
                value={characterAttack}
                onChange={(e) => setCharacterAttack(e.target.value)}
                style={{ flex: 1 }}
              />
              <TextField
                label="Accuracy"
                variant="outlined"
                margin="dense"
                size="small"
                type="number"
                value={characterAccuracy}
                onChange={(e) => setCharacterAccuracy(e.target.value)}
                style={{ flex: 1 }}
              />
            </Box>
            <Box display="flex" width="100%" gap="8px">
              <TextField
                label="Defense"
                variant="outlined"
                margin="dense"
                size="small"
                type="number"
                value={characterDefense}
                onChange={(e) => setCharacterDefense(e.target.value)}
                style={{ flex: 1 }}
              />
              <TextField
                label="Health"
                variant="outlined"
                margin="dense"
                size="small"
                type="number"
                value={characterHealth}
                onChange={(e) => setCharacterHealth(e.target.value)}
                style={{ flex: 1 }}
              />
            </Box>
            <Box display="flex" width="100%" alignItems="center" gap="8px">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={characterFinite}
                    onChange={(e) => setCharacterFinite(e.target.checked)}
                    size="small"
                  />
                }
                label="Finite"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={characterAvailableForPurchase}
                    onChange={(e) =>
                      setCharacterAvailableForPurchase(e.target.checked)
                    }
                    size="small"
                  />
                }
                label="Available for Purchase"
              />
            </Box>
            <Box display="flex" width="100%" gap="8px">
              <TextField
                label="Price"
                variant="outlined"
                margin="dense"
                size="small"
                type="number"
                value={characterPrice}
                onChange={(e) => setCharacterPrice(e.target.value)}
                style={{ flex: 1 }}
              />
              <TextField
                label="Season"
                variant="outlined"
                margin="dense"
                size="small"
                type="number"
                value={characterSeason}
                onChange={(e) => setCharacterSeason(e.target.value)}
                style={{ flex: 1 }}
              />
            </Box>
            <TextField
              label="Image URL"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={characterImageUrl}
              onChange={(e) => setCharacterImageUrl(e.target.value)}
            />
            <TextField
              label="Background Image"
              value={imageBG}
              onChange={(e) => setImageBG(e.target.value)}
              fullWidth
              margin="dense"
              size="small"
            />

            <Box display="flex" width="100%" gap="8px" mt={1}>
              <input
                type="color"
                value={characterBgColor1}
                onChange={(e) => setCharacterBgColor1(e.target.value)}
                style={{
                  width: "30%",
                  height: "38px",
                  borderRadius: "5px",
                  border: "none",
                }}
              />
              <input
                type="color"
                value={characterBgColor2}
                onChange={(e) => setCharacterBgColor2(e.target.value)}
                style={{
                  width: "30%",
                  height: "38px",
                  borderRadius: "5px",
                  border: "none",
                }}
              />
            </Box>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setCardType("")}
              style={{ marginTop: "16px", width: "80%" }}
              size="small"
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleUploadCard(cardType)}
              style={{ marginTop: "16px", width: "80%" }}
              size="small"
            >
              Submit
            </Button>
          </>
        ) : cardType === "infinity-csv" || cardType === "character-csv" ? (
          // CSV Upload Process Branch for both types
          <Box display="flex" flexDirection="column" alignItems="center">
            <p style={{ marginBottom: "16px" }}>
              What would you like to do next?
            </p>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="center"
              gap="16px"
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancelCSV}
                size="medium"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleUploadCards(uploadedCards, cardType)}
                size="medium"
              >
                Submit
              </Button>
            </Box>
          </Box>
        ) : (
          <div>Select a card type to begin</div>
        )}
      </Box>

      {/* Right Section */}
      <Box width="60vw" bgcolor="lightgray" p={2}>
        {cardType === "character" ? (
          <Grid
            container
            item
            xs={12}
            style={{
              justifyContent: "center",
              alignItems: "center",
              padding: 20,
              height: "100%",
            }}
          >
            <CharacterCard
              name={characterName}
              attackName={attackName}
              rarity={characterRarity}
              description={characterDescription}
              collectionName={characterCollectionName}
              tokenId={characterTokenId}
              attack={characterAttack}
              accuracy={characterAccuracy}
              defense={characterDefense}
              health={characterHealth}
              finite={characterFinite}
              availableForPurchase={characterAvailableForPurchase}
              price={characterPrice}
              season={characterSeason}
              imageUrl={characterImageUrl}
              imageStart={imageStart || ""}
              imageFin={imageFin || ""}
              imageHit={imageHit || ""}
              bgColor1={characterBgColor1 || "#fff"}
              bgColor2={characterBgColor2 || "#fff"}
              heightPer={80}
            />
          </Grid>
        ) : cardType === "character-csv" ? (
          <Grid
            container
            item
            xs={12}
            style={{ maxHeight: "100%", overflowY: "scroll" }}
          >
            {uploadedCards.map((card, index) => (
              <Grid item xs={12} md={3} key={index}>
                <CharacterCard
                  characterName={card.name}
                  attackName={card.attackName}
                  characterRarity={card.rarity}
                  characterDescription={card.description}
                  characterCollectionName={card.collectionName}
                  characterTokenId={card.tokenId}
                  characterAttack={card.attack}
                  characterAccuracy={card.accuracy}
                  characterDefense={card.defense}
                  characterHealth={card.health}
                  characterFinite={card.finite}
                  characterAvailableForPurchase={card.availForPurchase}
                  characterPrice={card.price}
                  characterSeason={card.season}
                  characterImageUrl={card.imageUrl}
                  pho={card.bgColor1 || "#fff"}
                  characterBgColor2={card.bgColor2 || "#fff"}
                  collectionStripeId={card.collectionStripeId}
                  collectionName={card.collectionName}
                  // Optionally pass heightPer and borderPx if needed:
                  heightPer={80}
                  borderPx={18}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setEditingCard(card);
                    setEditModalOpen(true);
                  }}
                  style={{ marginTop: "8px" }}
                >
                  Edit This
                </Button>
              </Grid>
            ))}
          </Grid>
        ) : cardType === "infinity" ? (
          <Grid
            container
            item
            xs={12}
            style={{
              justifyContent: "center",
              alignItems: "center",
              padding: 20,
              height: "100%",
            }}
          >
            <InfinityCard
              name={infinityName}
              description={infinityDescription}
              effect={infinityEffect}
              areaAffected={infinityAreaAffected}
              turnsActive={infinityTurnsActive}
              max={infinityMax}
              finite={infinityFinite}
              craftNeeded={infinityCraftNeeded}
              craftAmount={infinityCraftAmount}
              availableForPurchase={infinityAvailableForPurchase}
              price={infinityPrice}
              season={infinitySeason}
              bgColor1={infinityBgColor1}
              bgColor2={infinityBgColor2}
              imageUrl={infinityImageUrl}
              heightPer={80}
              borderPx={18}
            />
          </Grid>
        ) : cardType === "infinity-csv" ? (
          <Grid
            container
            item
            xs={12}
            style={{ maxHeight: "100%", overflowY: "scroll" }}
          >
            {uploadedCards.map((card, index) => (
              <Grid
                item
                container
                xs={12}
                md={4}
                key={index}
                style={{ height: "30%" }}
              >
                <Box style={{ width: "100%", aspectRatio: "2.5/3.5" }}>
                  <InfinityCard
                    name={card.name}
                    description={card.description}
                    effect={card.effect}
                    areaAffected={card.areaAffected}
                    turnsActive={card.turnsActive}
                    max={card.max}
                    finite={card.finite}
                    craftNeeded={card.craftNeeded}
                    craftAmount={card.craftAmount}
                    availableForPurchase={card.availForPurchase}
                    price={card.price}
                    season={card.season}
                    bgColor1={card.bgColor1 || "#fff"}
                    bgColor2={card.bgColor2 || "#fff"}
                    imageUrl={card.imageUrl}
                    heightPer={40}
                    borderPx={10}
                  />
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setEditingCard(card);
                    setEditModalOpen(true);
                  }}
                  style={{ marginTop: "8px" }}
                >
                  Edit
                </Button>
              </Grid>
            ))}
          </Grid>
        ) : (
          <div>Select a card type to begin</div>
        )}
      </Box>

      {/* Edit Modal for CSV-uploaded Infinity Cards */}
      {editModalOpen && editingCard && (
        <EditCardModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          card={editingCard}
          onUpdate={(updatedCard) => {
            let cardsCopy = [...uploadedCards];
            const index = cardsCopy.findIndex(
              (c) => c.name === editingCard.name
            );
            cardsCopy[index] = updatedCard;
            console.log("WE ARE UPDATING CARDS", cardsCopy);
            setUploadedCards(cardsCopy);

            setEditingCard(null);
            setEditModalOpen(false);
          }}
        />
      )}
    </Box>
  );
};

export default CardManager;
